import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Box } from '@mui/material';
import { colors } from '../constants';
import { CenteredLoader } from './States';

const ScreenLoadingContext = createContext(null);

/**
 * Section-wide loading state.
 *
 * A tab is the only thing that knows when it is loading, and the section is
 * the only thing that can cover every control — the date bar and the tab strip
 * as well as the content — so the two meet here: tabs report, the section
 * draws one loader over all of it and blocks every click until the load
 * finishes.
 *
 * This is for loads only: opening a tab, changing the date, switching
 * business. A refetch behind a save keeps its own non-blocking
 * `RefreshingOverlay`, so editing is never interrupted.
 */
export function ScreenLoadingProvider({ children }) {
  const [loading, setLoading] = useState({});

  // `null` reports that the view has finished loading.
  const report = useCallback((id, label) => {
    setLoading((prev) => {
      if (label === null) {
        if (!(id in prev)) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      }
      if (prev[id] === label) return prev;
      return { ...prev, [id]: label };
    });
  }, []);

  // Only one view is routed to at a time, so the first label is the label.
  const label = useMemo(() => Object.values(loading)[0], [loading]);

  return (
    <ScreenLoadingContext.Provider value={report}>
      <Box sx={{ position: 'relative', minHeight: label !== undefined ? '70vh' : undefined }}>
        {children}
        {label !== undefined ? <ScreenLoader label={label} /> : null}
      </Box>
    </ScreenLoadingContext.Provider>
  );
}

/**
 * Reports this view's load to the section. `label` is what the loader says;
 * pass `null` once the view has its data.
 */
export function useScreenLoading(id, label) {
  const report = useContext(ScreenLoadingContext);
  useEffect(() => {
    report?.(id, label);
    // Leaving the tab mid-load must not leave the loader behind.
    return () => report?.(id, null);
  }, [id, label, report]);
}

function ScreenLoader({ label }) {
  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 20,
      }}
    >
      {/* Veil and spinner are siblings so the opacity dims what is behind
          without washing out the indicator itself. */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundColor: colors.background,
          opacity: 0.6,
        }}
      />
      <CenteredLoader label={label} />
    </Box>
  );
}
